import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { provideNativeDateAdapter } from '@angular/material/core';
import { provideRouter } from '@angular/router';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { provideStore, Store } from '@ngxs/store';
import { AUTH_ENVIRONMENT, AuthService, TokenInjector } from '@shs-digi-care-monorepo/frontend-lib/auth';
import { provideShBreadcrumbRouting } from '@shs-digi-care-monorepo/frontend-lib/directives';
import { environment } from '../environments/environment';
import { LOGGING_CONFIG, LoggingInterceptor } from '@shs-digi-care-monorepo/frontend-lib/logging';
import { TeamState, UserActions, UserState } from '@shs-digi-care-monorepo/frontend-lib/states';
import { FILE_INPUT_ACCEPTED_TYPES } from '@shs-digi-care-monorepo/frontend-lib/ui/sh-file-input/sh-file-input.component';
import {
    CloudfrontTranslateLoader,
    EMEA_BROWSER_LOCALES,
    provideBrowserLocaleId
} from '@shs-digi-care-monorepo/frontend-lib/util';
import { TRANSLATION_SERVICE_NAME } from '@shs-digi-care-monorepo/frontend-lib';
import { ROLES, ServiceName } from '@shs-digi-care-monorepo/interfaces';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { provideAuth } from 'angular-auth-oidc-client';
import { provideEchartsCore } from 'ngx-echarts';
import { filter, Observable, take, tap } from 'rxjs';
import { APP_ROUTES } from './app.routes';
import { EMEA_REQUEST_ATTACHMENT_ACCEPT } from './shared/models/file-upload.constants';

ModuleRegistry.registerModules([AllCommunityModule]);

export function createTranslateLoader(http: HttpClient): TranslateLoader {
    return new CloudfrontTranslateLoader(http, `${environment.i18nBaseUrl}/${ServiceName.OmEmeaRequestService}`);
}

// App initialization factory function
function initializeAppFactory(): Observable<unknown> {
    const store = inject(Store);
    return inject(AuthService).isAuthenticated$.pipe(
        filter((isAuthenticated) => isAuthenticated),
        tap(() => {
            store.dispatch(new UserActions.LoadMe());
        }),
        take(1)
    );
}

// ECharts initialization factory
async function initializeEChartsFactory(): Promise<unknown> {
    const [echarts, { BarChart, PieChart }, { TitleComponent, TooltipComponent, GridComponent }, { SVGRenderer }] =
        await Promise.all([
            import('echarts/core'),
            import('echarts/charts'),
            import('echarts/components'),
            import('echarts/renderers')
        ]);

    echarts.use([BarChart, PieChart, TitleComponent, TooltipComponent, GridComponent, SVGRenderer]);
    return echarts;
}

// Application config
export const appConfig: ApplicationConfig = {
    providers: [
        provideRouter(APP_ROUTES),
        importProvidersFrom(
            TranslateModule.forRoot({
                defaultLanguage: 'en-us',
                loader: {
                    provide: TranslateLoader,
                    useFactory: createTranslateLoader,
                    deps: [HttpClient]
                }
            })
        ),
        ...(environment.production
            ? []
            : [importProvidersFrom(NgxsReduxDevtoolsPluginModule.forRoot({ trace: true }))]),
        provideAuth({
            config: environment.authentication
        }),
        provideEchartsCore({ echarts: initializeEChartsFactory() }),
        provideStore([UserState, TeamState], {
            developmentMode: !environment.production,
            compatibility: { strictContentSecurityPolicy: true }
        }),
        provideAppInitializer(() => initializeAppFactory()),
        provideShBreadcrumbRouting(),
        {
            provide: 'ROLES',
            useValue: ROLES
        },
        {
            provide: 'RESOURCES',
            useValue: []
        },
        { provide: HTTP_INTERCEPTORS, useClass: TokenInjector, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: LoggingInterceptor, multi: true },
        { provide: AUTH_ENVIRONMENT, useValue: environment },
        { provide: LOGGING_CONFIG, useValue: { stage: environment.stage } },
        { provide: FILE_INPUT_ACCEPTED_TYPES, useValue: EMEA_REQUEST_ATTACHMENT_ACCEPT },
        { provide: TRANSLATION_SERVICE_NAME, useValue: ServiceName.OmEmeaRequestService },
        provideBrowserLocaleId('en-GB', { supportedLocales: EMEA_BROWSER_LOCALES }),
        provideNativeDateAdapter(),
        provideHttpClient(withInterceptorsFromDi())
    ]
};
